import React, { useCallback, useState } from "react"

import type {
  BucketClickData,
  DashboardWidget,
  DashboardWidgetConfig,
} from "../../types/core"
import type { WidgetMoveTarget } from "../../utils/dashboardFlowMoves"
import { injectGridStyles } from "../../utils/gridStyles"
import { AlertFromWidgetModal } from "../AlertFromWidgetModal"
import { FlowDashboardGrid } from "./FlowDashboardGrid"
import { GridDashboardWidget } from "./GridDashboardWidget"

injectGridStyles()

interface GridDashboardLayoutProps {
  widgets: DashboardWidget[]
  dashboardId: string
  onRemove: (id: string) => void
  onConfigure: (id: string) => void
  onClone?: (id: string) => void
  onConfigChange: (id: string, config: DashboardWidgetConfig) => void
  onMoveWidget?: (activeWidgetId: string, target: WidgetMoveTarget) => void
  onWidthChange?: (id: string, columns: number) => void
  onPointClick?: (
    point: BucketClickData,
    widgetId: string,
    metricId?: string,
  ) => void
  onChartClick?: (
    point: BucketClickData,
    widgetId: string,
    metricId?: string,
  ) => void
  getSelectedPointForWidget?: (widgetId: string) => BucketClickData | null
}

export const GridDashboardLayout: React.FC<GridDashboardLayoutProps> = ({
  widgets,
  dashboardId,
  onRemove,
  onConfigure,
  onClone,
  onConfigChange,
  onChartClick,
  onMoveWidget,
  onWidthChange,
  onPointClick,
  getSelectedPointForWidget,
}) => {
  const [alertModalWidget, setAlertModalWidget] =
    useState<DashboardWidget | null>(null)

  const handleAlert = useCallback(
    (widgetId: string) => {
      const widget = widgets.find((w) => w.id === widgetId)
      if (widget) {
        setAlertModalWidget(widget)
      }
    },
    [widgets],
  )

  const handleCloseAlertModal = useCallback(() => {
    setAlertModalWidget(null)
  }, [])

  const renderWidget = useCallback(
    (widget: DashboardWidget) => (
      <GridDashboardWidget
        widget={widget}
        dashboardId={dashboardId}
        onRemove={onRemove}
        onConfigure={onConfigure}
        onClone={onClone}
        onAlert={handleAlert}
        onConfigChange={onConfigChange}
        onWidthChange={onWidthChange}
        onChartClick={onChartClick}
        onPointClick={onPointClick}
        getSelectedPointForWidget={getSelectedPointForWidget}
      />
    ),
    [
      dashboardId,
      onRemove,
      onConfigure,
      onClone,
      handleAlert,
      onConfigChange,
      onWidthChange,
      onChartClick,
      onPointClick,
      getSelectedPointForWidget,
    ],
  )

  return (
    <>
      <FlowDashboardGrid
        widgets={widgets}
        renderWidget={renderWidget}
        onMoveWidget={onMoveWidget}
        onWidthChange={onWidthChange}
      />

      {alertModalWidget && (
        <AlertFromWidgetModal
          isOpen={!!alertModalWidget}
          onClose={handleCloseAlertModal}
          widget={alertModalWidget}
        />
      )}
    </>
  )
}
